import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { childrenShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import Context from '@bibliocommons/context';
import UserCard from '@bibliocommons/user-card';

import { createFollow, createIgnore, removeFollow, removeIgnore } from 'app/actions/RelationshipsActions';
import {
  selectSyncingFollowsByTargetUserId,
  selectSyncingIgnoresByTargetUserId,
  selectUserIdToFollowedUserId,
  selectUserIdToIgnoredUserId,
  selectIsFetchingRelationships
} from 'app/selectors/RelationshipsSelector';

export default function UserCardContainer({ children, targetUser }) {
  const dispatch = useDispatch();
  const isFetchingRelationships = useSelector(selectIsFetchingRelationships);
  const { mobileApp: isMobileApp } = useContext(Context);
  const syncingFollowsByTargetUserId = useSelector(selectSyncingFollowsByTargetUserId);
  const syncingIgnoresByTargetUserId = useSelector(selectSyncingIgnoresByTargetUserId);
  const userIdToFollowedUserId = useSelector(selectUserIdToFollowedUserId);
  const userIdToIgnoredUserId = useSelector(selectUserIdToIgnoredUserId);

  const userId = targetUser.get('id');
  const isFollowing = userIdToFollowedUserId.has(userId);
  const isIgnoring = userIdToIgnoredUserId.has(userId);
  const isSyncingFollowForTargetUser = isFetchingRelationships || syncingFollowsByTargetUserId.has(userId);
  const isSyncingIgnoreForTargetUser = isFetchingRelationships || syncingIgnoresByTargetUserId.has(userId);

  const toggleIsFollowing = () => {
    if (isFollowing) {
      const followId = userIdToFollowedUserId.get(userId, null);
      if (!followId) {
        throw new Error('A followId is required to unfollow a user');
      }

      dispatch(
        removeFollow({
          followId,
          targetUserId: userId
        })
      );
    } else {
      dispatch(
        createFollow({
          targetUserId: userId
        })
      );
    }
  };

  const toggleIsIgnoring = () => {
    if (isIgnoring) {
      const ignoreId = userIdToIgnoredUserId.get(userId, null);
      if (!ignoreId) {
        throw new Error('An ignoreId is required to unignore a user');
      }

      dispatch(
        removeIgnore({
          ignoreId,
          targetUserId: userId
        })
      );
    } else {
      dispatch(
        createIgnore({
          targetUserId: userId
        })
      );
    }
  };

  return (
    <UserCard
      isFollowing={isFollowing}
      isIgnoring={isIgnoring}
      isMobileApp={isMobileApp}
      isSyncingFollowingState={isSyncingFollowForTargetUser}
      isSyncingIgnoringState={isSyncingIgnoreForTargetUser}
      offset={[0, -4]}
      toggleIsFollowing={toggleIsFollowing}
      toggleIsIgnoring={toggleIsIgnoring}
      userIdentity={targetUser}
    >
      {children}
    </UserCard>
  );
}

UserCardContainer.propTypes = {
  children: childrenShape.isRequired,
  targetUser: userIdentityShape.isRequired
};
